import React, { useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

import { Area, CartesianGrid, ComposedChart, Line, ReferenceDot, ResponsiveContainer, XAxis, YAxis, useXAxisScale, useYAxisScale } from "recharts";

import { formatNumericCurrency, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { Svg, Typography } from "components";

import {
  OnboardingForecastPoint,
  getForecastYAxisTicks,
  getForecastYDomain,
  getForecastYearTicks,
} from "pages/app/scenario-onboarding/utils/onboardingForecast";
import { getAnonymousForecastCumulativeDelta, getDeltaHighlightBand } from "pages/app/soilsight-pre-login/utils/acquisitionForecast";

const BASELINE_COLOR = "#065c45"; // $green-400
const CHANGE_COLOR = "#4d85f5"; // $blue-500
// 🚨 Recharts cannot read SCSS. We mirror $berry-400 from variables.scss here.
const BERRY_400 = "#9a5c7a";
const DELTA_OPACITY = 0.2;
const PHONE_MEDIA = "(max-width: 768px)";

const CALLOUT_WIDTH = 210;
const MIN_BRACKET_HEIGHT = 4;

const subscribeToPhoneMedia = (onStoreChange: () => void) => {
  const mediaQuery = window.matchMedia(PHONE_MEDIA);
  mediaQuery.addEventListener("change", onStoreChange);
  return () => mediaQuery.removeEventListener("change", onStoreChange);
};

const getPhoneMediaSnapshot = () => window.matchMedia(PHONE_MEDIA).matches;

const CHART_LAYOUT = {
  desktop: {
    height: 306,
    marginTop: 32,
    marginRight: 8,
    xAxisPaddingRight: 48,
    yAxisWidth: 72,
    endMarkerRadius: 6.5,
    yearTicks: [1, 3, 5, 8, 10],
    calloutCaretWidth: 34,
    calloutCaretHeight: 5,
    calloutGapAboveMarker: 12,
    bracketCapWidth: 18,
  },
  phone: {
    height: 272,
    marginTop: 72,
    marginRight: 16,
    xAxisPaddingRight: 40,
    yAxisWidth: "auto",
    endMarkerRadius: 5,
    yearTicks: [1, 5, 10],
    calloutCaretWidth: 24,
    calloutCaretHeight: 4,
    calloutGapAboveMarker: 10,
    bracketCapWidth: 12,
  },
} as const;

type ChartLayout = (typeof CHART_LAYOUT)[keyof typeof CHART_LAYOUT];

const GapBracket = ({ anchorX, innerTop, height, capWidth }: { anchorX: number; innerTop: number; height: number; capWidth: number }) => {
  const inset = 0.75;
  const midX = capWidth / 2;

  return (
    <svg
      className="soilsight-acquisition-forecast__gap-bracket"
      data-testid="soilsight-forecast-gap-bracket"
      width={capWidth}
      height={height}
      style={{ left: anchorX - capWidth / 2, top: innerTop }}
      aria-hidden
    >
      <g stroke={BERRY_400} strokeWidth="1.5" strokeLinecap="round">
        <line x1={midX} y1={inset} x2={midX} y2={height - inset} />
        <line x1={0} y1={inset} x2={capWidth} y2={inset} />
        <line x1={0} y1={height - inset} x2={capWidth} y2={height - inset} />
      </g>
    </svg>
  );
};

type GapCalloutOverlayProps = {
  gapLabel: string;
  yearCount: number;
  lastPoint: OnboardingForecastPoint;
  layout: ChartLayout;
  hostEl: HTMLDivElement | null;
};

// We render this inside ComposedChart so we can use the scale hooks. We portal the HTML out of the SVG because foreignObject is unreliable.
const GapCalloutOverlay = ({ gapLabel, yearCount, lastPoint, layout, hostEl }: GapCalloutOverlayProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();

  if (!hostEl || lastPoint.changeCumulative == null || !xScale || !yScale) return null;

  const anchorX = xScale(lastPoint.yearNum);
  const baselineY = yScale(lastPoint.baselineCumulative);
  const changeY = yScale(lastPoint.changeCumulative);
  if (anchorX == null || baselineY == null || changeY == null) return null;

  const hostWidth = hostEl.clientWidth;
  const unclampedLeft = anchorX - CALLOUT_WIDTH / 2;
  const left = hostWidth > 0 ? Math.max(0, Math.min(unclampedLeft, hostWidth - CALLOUT_WIDTH)) : unclampedLeft;
  // We pin the caret tip above the marker. The body grows upward from there with translateY(-100%) in the stylesheet.
  const top = Math.min(baselineY, changeY) - layout.calloutGapAboveMarker;
  const caretLeft = anchorX - left - layout.calloutCaretWidth / 2;

  const innerTop = Math.min(baselineY, changeY) + layout.endMarkerRadius;
  const bracketHeight = Math.max(baselineY, changeY) - layout.endMarkerRadius - innerTop;

  return createPortal(
    <>
      {bracketHeight > MIN_BRACKET_HEIGHT ? (
        <GapBracket anchorX={anchorX} innerTop={innerTop} height={bracketHeight} capWidth={layout.bracketCapWidth} />
      ) : null}
      <div
        className="soilsight-acquisition-forecast__gap-callout"
        data-testid="soilsight-forecast-gap-callout"
        style={{ left, top, width: CALLOUT_WIDTH, paddingBottom: layout.calloutCaretHeight }}
      >
        <div className="soilsight-acquisition-forecast__gap-callout-body">
          <Svg svgName="soilsight-pre-login/forecast-callout-bracket" className="soilsight-acquisition-forecast__gap-callout-icon" />
          <div className="soilsight-acquisition-forecast__gap-callout-copy">
            <Typography variant="paragraph-l" bold noWrap className="soilsight-acquisition-forecast__gap-callout-value">
              {t("preLogin.simulation.forecast.calloutValue", { amount: gapLabel })}
            </Typography>
            <Typography variant="paragraph-xs" color="grey-600" className="soilsight-acquisition-forecast__gap-callout-description">
              {t("preLogin.calloutDescription", { count: yearCount })}
            </Typography>
          </div>
        </div>
        <div
          className="soilsight-acquisition-forecast__gap-callout-arrow"
          style={{ width: layout.calloutCaretWidth, height: layout.calloutCaretHeight, left: caretLeft }}
          aria-hidden
        >
          <Svg
            svgName="soilsight-pre-login/forecast-callout-caret"
            className="soilsight-acquisition-forecast__gap-callout-arrow-shape"
            width={layout.calloutCaretWidth}
            height={layout.calloutCaretHeight}
          />
        </div>
      </div>
    </>,
    hostEl
  );
};

type AcquisitionForecastChartProps = {
  data: OnboardingForecastPoint[];
  currency: CurrencyCode;
  gapLabel: string | null;
};

export const AcquisitionForecastChart = ({ data, currency, gapLabel }: AcquisitionForecastChartProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const isPhone = useSyncExternalStore(subscribeToPhoneMedia, getPhoneMediaSnapshot);
  const [chartHostEl, setChartHostEl] = useState<HTMLDivElement | null>(null);

  if (data.length === 0) return null;

  const layout = isPhone ? CHART_LAYOUT.phone : CHART_LAYOUT.desktop;
  const lastPoint = data[data.length - 1];
  const xTicks = getForecastYearTicks(data.length, [...layout.yearTicks]);
  const yTicks = getForecastYAxisTicks(data);
  const yDomain = getForecastYDomain(data, yTicks);
  const practicesAheadAtEnd = lastPoint.changeCumulative != null && getAnonymousForecastCumulativeDelta(data) >= 0;
  const chartData = data.map((point) => ({ ...point, deltaHighlight: getDeltaHighlightBand(point, practicesAheadAtEnd) }));

  return (
    <div ref={setChartHostEl} className="soilsight-acquisition-forecast__chart" data-testid="soilsight-acquisition-forecast-chart">
      <ResponsiveContainer width="100%" height={layout.height}>
        <ComposedChart data={chartData} margin={{ top: layout.marginTop, right: layout.marginRight, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="4 4" vertical={false} />
          <XAxis
            dataKey="yearNum"
            ticks={xTicks}
            tickFormatter={(yearNum: number) => t("preLogin.simulation.forecast.yearTick", { year: yearNum })}
            tickLine={false}
            axisLine={false}
            padding={{ right: layout.xAxisPaddingRight }}
          />
          <YAxis
            domain={yDomain}
            ticks={yTicks}
            tickFormatter={(value: number) => formatNumericCurrency(value, { decimals: 0, currency, currencyDisplay: "narrowSymbol" })}
            tickLine={false}
            axisLine={false}
            width={layout.yAxisWidth}
          />
          <Area
            type="monotone"
            dataKey="deltaHighlight"
            stroke="none"
            fill={BERRY_400}
            fillOpacity={DELTA_OPACITY}
            isAnimationActive={false}
            activeDot={false}
          />
          <Line type="monotone" dataKey="baselineCumulative" stroke={BASELINE_COLOR} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="changeCumulative" stroke={CHANGE_COLOR} strokeWidth={2} dot={false} isAnimationActive={false} />
          <ReferenceDot x={lastPoint.yearNum} y={lastPoint.baselineCumulative} r={layout.endMarkerRadius} fill={BASELINE_COLOR} stroke="none" />
          {lastPoint.changeCumulative != null && (
            <ReferenceDot x={lastPoint.yearNum} y={lastPoint.changeCumulative} r={layout.endMarkerRadius} fill={CHANGE_COLOR} stroke="none" />
          )}
          {gapLabel ? <GapCalloutOverlay gapLabel={gapLabel} yearCount={data.length} lastPoint={lastPoint} layout={layout} hostEl={chartHostEl} /> : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
